import { FC, useState } from 'react';
import {
  HookQuerySearchSelect,
  HookQuerySearchSelectProps,
} from 'ui-component/HookFormComponents';
import ContextualCreateButton from 'ui-component/ContextualCreateButton';
import { UseFormSetValue, FieldValues } from 'react-hook-form';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import { QueryParams } from 'types/api';
import { TeamSchema } from 'types/clientV2/teams';
import { useGetTeamsClientV2Query } from 'store/slices/clientV2/teams';
import CreateTeamDialog from 'views/properties/Teams/components/CreateTeamDialog';

type OmitHookQuerySearchSelectProps = Omit<
  HookQuerySearchSelectProps<TeamSchema>,
  'schema' | 'useGetQuery' | 'getOptionLabel'
>;

type BaseTeamAutocompleteSearchInputProps = {
  multiple?: boolean;
  schema?: QueryParams<TeamSchema>['schema'];
  getOptionLabel?: (option: TeamSchema) => string;
  formDefaults?: Record<string, string | boolean | string[] | null>;
} & HookFormComponentProps &
  OmitHookQuerySearchSelectProps;

type ContextualCreateTrueProps = BaseTeamAutocompleteSearchInputProps & {
  contextualCreate: true;
  setValue: UseFormSetValue<FieldValues>;
};

type ContextualCreateFalseProps = BaseTeamAutocompleteSearchInputProps & {
  contextualCreate?: false;
  setValue?: UseFormSetValue<FieldValues>;
};

type TeamAutocompleteInputProps =
  | ContextualCreateTrueProps
  | ContextualCreateFalseProps;

const HookFormTeamAutocompleteQuery: FC<TeamAutocompleteInputProps> = ({
  errors,
  control,
  name,
  setValue,
  schema,
  label,
  multiple = false,
  filters,
  excludes,
  getOptionLabel,
  contextualCreate = false,
  disabled,
  formDefaults,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contextuallyCreatedOption, setContextuallyCreatedOption] = useState<
    TeamSchema | undefined
  >();

  const QuerySearchSelect = (
    <HookQuerySearchSelect<TeamSchema>
      sx={{ width: '100%' }}
      name={name}
      label={label}
      useGetQuery={useGetTeamsClientV2Query}
      filters={filters}
      multiple={multiple}
      contextuallyCreatedOption={contextuallyCreatedOption}
      schema={schema || ['name']}
      excludes={excludes}
      errors={errors}
      control={control}
      disabled={disabled}
      getOptionLabel={
        getOptionLabel || ((option: TeamSchema) => option?.name ?? '')
      }
    />
  );

  return (
    <>
      {contextualCreate ? (
        <>
          <ContextualCreateButton
            onClick={() => setDialogOpen(true)}
            boxAlignItems="flex-start"
            buttonMarginTop={0.5}
          >
            {QuerySearchSelect}
          </ContextualCreateButton>
          <CreateTeamDialog
            dialogOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onCreateSuccess={(newTeam) => {
              const team = newTeam as TeamSchema;
              setContextuallyCreatedOption(team);
              setValue && setValue(name, team.id);
            }}
          />
        </>
      ) : (
        QuerySearchSelect
      )}
    </>
  );
};

export { HookFormTeamAutocompleteQuery };
